import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "../../environments/environment";

export type IssueTag = "PMG" | "PRAGATI";
export type IssueStatus = "Pending" | "Resolved" | "Forwarded";
export type IssueActivityType = "comment" | "decision" | "update";

export interface IssueItem {
  id?: number;
  project?: number;
  project_name?: string;
  agency?: number;
  agency_name?: string;
  tag?: IssueTag;
  title?: string;
  description?: string;
  recipient_state?: string;
  responsible_authority?: string;
  issue_category?: string;
  status?: IssueStatus;
  created_at?: string;
  updated_at?: string;
}

export interface IssueListResponse {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: IssueItem[];
}

export interface ListIssuesQuery {
  project?: string;
  agency?: string;
  status?: string;
  search?: string;
}

export interface CreateIssuePayload {
  project: number;
  agency: number;
  tag: IssueTag;
  title: string;
  description: string;
  recipient_state: string;
  responsible_authority: string;
  issue_category: string;
  status: IssueStatus;
}

export interface AgencyItem {
  id: number;
  name: string;
}

export interface IssueDetail extends IssueItem {
  project_name?: string;
  agency_name?: string;
  activities?: any[];
}

export interface IssueCommentItem {
  id?: number;
  issue?: number;
  activity_type?: IssueActivityType;
  message?: string;
  created_at?: string;
  user?: any;
  user_name?: string;
  created_by?: any;
}

export interface CreateActivityPayload {
  issue: number;
  activity_type: IssueActivityType;
  message: string;
}

@Injectable({ providedIn: "root" })
export class ProjectIssuesService {
  private readonly baseUrl = environment.baseUrl + "/api/project";

  constructor(private http: HttpClient) { }

  listIssues(query: ListIssuesQuery): Observable<IssueItem[]> {
    let params = new HttpParams();

    if (query?.project) params = params.set("project", query.project);
    if (query?.agency) params = params.set("agency", query.agency);
    if (query?.status) params = params.set("status", query.status);
    if (query?.search) params = params.set("search", query.search);

    return this.http
      .get<IssueListResponse | IssueItem[]>(this.baseUrl + "/v1/issues/", { params })
      .pipe(map((res: any) => (Array.isArray(res) ? res : res?.results || [])));
  }

  getIssueDetail(issueId: number | string): Observable<IssueDetail> {
    return this.http.get<IssueDetail>(this.baseUrl + "/v1/issues/" + issueId + "/");
  }

  createIssue(payload: CreateIssuePayload): Observable<IssueItem> {
    return this.http.post<IssueItem>(this.baseUrl + "/v1/issues/", payload);
  }

  getAgencies(): Observable<AgencyItem[]> {
    return this.http.get<AgencyItem[]>(this.baseUrl + "/v1/filter/agencies");
  }

  getIssueComments(issueId: number | string): Observable<IssueCommentItem[]> {
    const params = new HttpParams().set("issue_id", String(issueId));
    return this.http.get<IssueCommentItem[]>(this.baseUrl + "/v1/issues/comments", { params });
  }

  createIssueActivity(payload: CreateActivityPayload): Observable<IssueCommentItem> {
    return this.http.post<IssueCommentItem>(this.baseUrl + "/v1/issue/create-activity", payload);
  }

  deleteIssueComment(activityId: number | string): Observable<any> {
    return this.http.delete(this.baseUrl + "/v1/issues/comments/" + activityId);
  }
}
